import { useCallback, useEffect, useMemo } from "react";

import { Timestamp } from "firebase/firestore";

import { DateTime } from "luxon";

import type {
	ChatMessage,
	ChatModel,
	DBChatActivity,
	DBChatMessage,
	DBChatModel,
	DBConversationParticipant,
	MessagesSortKey,
	MessagesWindow
} from "shared/types/Messages";
import { MessagePositions, MessageStatuses, messagingConfig, messagingDBConfig } from "shared/types/Messages";

import { getDateTime } from "utils/getDateTime";

import { collectCursorValues, requestToPromise } from "utils/serviceUtils/dbUtils";
import { uniqueifyData } from "utils/uniqueifyData";

import useMessagingDBEvents from "./useMessagingDBEvents";

import { useUser } from "./index";

const messageSortKey = (message: Pick<DBChatMessage, "conversationId" | "sentAt" | "id">): MessagesSortKey => {
	return [message.conversationId, message.sentAt, message.id];
};

const useMessagingDB = () => {
	const { emit: emitDbEvent, destroy } = useMessagingDBEvents();

	const { getData: getUserData } = useUser();
	const { myProfile } = getUserData();

	useEffect(() => {
		return () => {
			destroy();
		};
	}, [destroy]);

	const convertChatMessageToDBMessage = useCallback(
		(message: ChatMessage, conversationId: string): DBChatMessage => {
			let sentAt = 0;
			if (message?.sentAt) {
				sentAt = getDateTime(message.sentAt).toMillis();
			}

			let editedAt: null | number = null;
			if (message?.editedAt) {
				editedAt = getDateTime(message.editedAt).toMillis();
			}

			let removedAt: null | number = null;
			if (message?.removedAt) {
				removedAt = getDateTime(message.removedAt).toMillis();
			}

			return {
				...message,
				sentAt,
				editedAt,
				removedAt,
				conversationId,
				// status: message.id !== MessageStatuses.PLACEHOLDER ? MessageStatuses.READ : MessageStatuses.WAITING,
				status: message?.tempStatus ?? MessageStatuses.READ,
				position: message.sender.id === myProfile?.id ? MessagePositions.RIGHT : MessagePositions.LEFT
			};
		},
		[myProfile?.id]
	);

	const openDb = useCallback((): Promise<IDBDatabase> => {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(messagingDBConfig.name, messagingDBConfig.version);

			request.onupgradeneeded = () => {
				const db = request.result;

				if (!db.objectStoreNames.contains(messagingDBConfig.stores.conversations.name)) {
					const conversationsStore = db.createObjectStore(messagingDBConfig.stores.conversations.name, {
						keyPath: "id"
					});
					conversationsStore.createIndex("updatedAt", "updatedAt", { unique: false });
					conversationsStore.createIndex("lastOpenedAt", "lastOpenedAt", { unique: false });
				}

				if (!db.objectStoreNames.contains(messagingDBConfig.stores.messages.name)) {
					const messagesStore = db.createObjectStore(messagingDBConfig.stores.messages.name, { keyPath: "id" });
					messagesStore.createIndex("conversationId", "conversationId", { unique: false });
					messagesStore.createIndex("tag", "tag", { unique: true });
					messagesStore.createIndex("sentAt", "sentAt", { unique: false });
					messagesStore.createIndex("removedAt", "removedAt", { unique: false });
					messagesStore.createIndex("senderId", "senderId", { unique: false });
					messagesStore.createIndex(messagingDBConfig.stores.messages.sortIndex, ["conversationId", "sentAt", "id"], {
						unique: false
					});
				}

				if (!db.objectStoreNames.contains(messagingDBConfig.stores.participants.name)) {
					const usersStore = db.createObjectStore(messagingDBConfig.stores.participants.name, { keyPath: "id" });
					usersStore.createIndex("updatedAt", "updatedAt", { unique: false });
				}

				if (!db.objectStoreNames.contains(messagingDBConfig.stores.drafts.name)) {
					const draftsStore = db.createObjectStore(messagingDBConfig.stores.drafts.name, { keyPath: "id" });
					draftsStore.createIndex("conversationId", "conversationId", { unique: false });
				}

				if (!db.objectStoreNames.contains(messagingDBConfig.stores.activities.name)) {
					const activitiesStore = db.createObjectStore(messagingDBConfig.stores.activities.name, { keyPath: "id" });
					activitiesStore.createIndex("conversationId", "conversationId", { unique: false });
					activitiesStore.createIndex("senderId", "senderId", { unique: false });
					activitiesStore.createIndex("sentAt", "sentAt", { unique: false });
				}
			};

			request.onsuccess = () => resolve(request.result);
			request.onerror = () => reject(request.error);
		});
	}, []);

	const methods = useMemo(
		() => ({
			updateConversations: async (conversations: ChatModel[], removeExisted = false): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.conversations.name, "readwrite");

				const conversationStore = tx.objectStore(messagingDBConfig.stores.conversations.name);

				for (const conversation of conversations) {
					let updatedAt = 0;
					if (conversation.lastMessage?.sentAt) {
						updatedAt = getDateTime(conversation.lastMessage.sentAt).toMillis();
					}

					let lastOpenedAt = 0;
					if (conversation.lastOpenedAt) {
						lastOpenedAt = getDateTime(conversation.lastOpenedAt).toMillis();
						if (!updatedAt) {
							updatedAt = lastOpenedAt;
						}
					}

					const existingConversation = (await requestToPromise(conversationStore.get(conversation.id))) as
						| DBChatModel
						| undefined;

					conversationStore.put({
						...existingConversation,
						...conversation,
						updatedAt,
						lastOpenedAt,
						lastMessage: conversation?.lastMessage
							? convertChatMessageToDBMessage(conversation.lastMessage, conversation.id)
							: (existingConversation?.lastMessage ?? null)
					});
				}

				if (removeExisted) {
					const conversationIds = new Set(conversations.map(c => c.id));
					const allConversations = await requestToPromise(conversationStore.getAll());

					for (const existingConversation of allConversations as DBChatModel[]) {
						if (!conversationIds.has(existingConversation.id)) {
							conversationStore.delete(existingConversation.id);
						}
					}
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "conversations-changed" });
			},
			updateMessages: async (messages: ChatMessage[], conversationId: string): Promise<void> => {
				if (messages.length) {
					const db = await openDb();
					const tx = db.transaction(
						[messagingDBConfig.stores.conversations.name, messagingDBConfig.stores.messages.name],
						"readwrite"
					);

					const conversationsStore = tx.objectStore(messagingDBConfig.stores.conversations.name);
					const messagesStore = tx.objectStore(messagingDBConfig.stores.messages.name);

					const updatedMessages: DBChatMessage[] = [];
					for (const message of messages) {
						const updatedMessage = convertChatMessageToDBMessage(message, conversationId);
						updatedMessages.push(updatedMessage);
						messagesStore.put(updatedMessage);
					}

					const notRemovedMessages = updatedMessages.filter(message => !message.removedAt);
					const lastMessage = notRemovedMessages.length ? notRemovedMessages[notRemovedMessages.length - 1]! : null;

					const conversation = (await requestToPromise(conversationsStore.get(conversationId))) as
						| DBChatModel
						| undefined;
					if (conversation && lastMessage) {
						// const nextUnreadCount =
						// 	lastMessage.status === MessageStatuses.OUTGOING
						// 		? conversation.unreadCount
						// 		: lastMessage.sentAt > (conversation.lastOpenedAt ?? 0)
						// 			? conversation.unreadCount + 1
						// 			: conversation.unreadCount;

						conversationsStore.put({
							...conversation,
							updatedAt: lastMessage.sentAt,
							lastMessage
							// unreadCount: nextUnreadCount
						});
					}

					await new Promise<void>((resolve, reject) => {
						tx.oncomplete = () => resolve();
						tx.onerror = () => reject(tx.error);
						tx.onabort = () => reject(tx.error);
					});

					db.close();

					if (conversation && lastMessage) {
						emitDbEvent({ type: "conversations-changed" });
					}

					emitDbEvent({
						type: "messages-changed",
						conversation: {
							id: conversationId,
							messages: updatedMessages
						}
					});
				}
			},
			updateParticipants: async (participants: DBConversationParticipant[]) => {
				if (participants.length) {
					const db = await openDb();
					const tx = db.transaction(messagingDBConfig.stores.participants.name, "readwrite");
					const store = tx.objectStore(messagingDBConfig.stores.participants.name);

					for (const participant of participants) {
						store.put(participant);
					}

					await new Promise<void>((resolve, reject) => {
						tx.oncomplete = () => resolve();
						tx.onerror = () => reject(tx.error);
						tx.onabort = () => reject(tx.error);
					});

					db.close();
					emitDbEvent({ type: "participants-changed", participantIds: participants.map(user => user.id) });
				}
			},
			getDraft: async (conversationId: string, personId: string): Promise<null | DBChatMessage> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.drafts.name, "readonly");

				const draftsStore = tx.objectStore(messagingDBConfig.stores.drafts.name);

				// Find drafts with specific senderId and conversationId
				const conversationIdIndex = draftsStore.index("conversationId");
				const existedDraft = await new Promise<null | DBChatMessage>((resolve, reject) => {
					let result: null | DBChatMessage = null;
					const request = conversationIdIndex.openCursor(IDBKeyRange.only(conversationId));

					request.onsuccess = () => {
						const cursor = request.result;
						if (!cursor) {
							resolve(result);
							return;
						}
						const existedDraft = cursor.value as DBChatMessage;
						if (existedDraft.sender.id === personId) {
							result = existedDraft;
							resolve(result);
							return;
						}
						cursor.continue();
					};

					request.onerror = () => reject(request.error);
				});

				db.close();

				return existedDraft;
			},
			updateDrafts: async (draft: ChatMessage, conversationId: string): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.drafts.name, "readwrite");

				const draftsStore = tx.objectStore(messagingDBConfig.stores.drafts.name);

				const conversationIdIndex = draftsStore.index("conversationId");
				const existedDraft = (await requestToPromise(conversationIdIndex.get(conversationId))) as DBChatMessage;

				if (existedDraft) {
					draftsStore.delete(existedDraft.id);
				}

				if (!draft.removedAt) {
					draftsStore.put(convertChatMessageToDBMessage(draft, conversationId));
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "drafts-changed", conversationId });
			},
			deleteDraft: async (params: { conversationId: string; personId: string }): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.drafts.name, "readwrite");

				const draftsStore = tx.objectStore(messagingDBConfig.stores.drafts.name);

				// Find drafts with specific senderId and conversationId
				const conversationIdIndex = draftsStore.index("conversationId");
				const existingDrafts = await new Promise<DBChatMessage[]>((resolve, reject) => {
					const results: DBChatMessage[] = [];
					const request = conversationIdIndex.openCursor(IDBKeyRange.only(params.conversationId));

					request.onsuccess = () => {
						const cursor = request.result;
						if (!cursor) {
							resolve(results);
							return;
						}
						const existedDraft = cursor.value as DBChatMessage;
						if (existedDraft.sender.id === params.personId) {
							results.push(existedDraft);
						}
						cursor.continue();
					};

					request.onerror = () => reject(request.error);
				});

				for (const draft of existingDrafts) {
					draftsStore.delete(draft.id);
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "drafts-changed", conversationId: params.conversationId });
			},
			getActivities: async (conversationId: string): Promise<DBChatActivity[]> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.activities.name, "readonly");

				const activitiesStore = tx.objectStore(messagingDBConfig.stores.activities.name);

				// Find drafts with specific senderId and conversationId
				const conversationIdIndex = activitiesStore.index("conversationId");
				const activities = await collectCursorValues<DBChatActivity>(() =>
					conversationIdIndex.openCursor(IDBKeyRange.only(conversationId))
				);

				db.close();

				return activities;
			},
			updateActivities: async (activities: DBChatActivity[]): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.activities.name, "readwrite");

				const activitiesStore = tx.objectStore(messagingDBConfig.stores.activities.name);

				// Remove old activities > 10 mins
				const tenMinutesAgo = DateTime.now().minus({ minute: 10 }).toMillis();
				const sentAtIndex = activitiesStore.index("sentAt");
				const oldActivities = await new Promise<DBChatActivity[]>((resolve, reject) => {
					const results: DBChatActivity[] = [];
					const request = sentAtIndex.openCursor(IDBKeyRange.upperBound(tenMinutesAgo));

					request.onsuccess = () => {
						const cursor = request.result;
						if (!cursor) {
							resolve(results);
							return;
						}
						results.push(cursor.value as DBChatActivity);
						cursor.continue();
					};

					request.onerror = () => reject(request.error);
				});

				if (activities.length === 1) {
					const conversationId = activities[0]!.conversationId;
					const personId = activities[0]!.person.id;

					// Find existing activities with specific conversationId and personId
					const conversationIdIndex = activitiesStore.index("conversationId");
					const existingActivities = await new Promise<DBChatActivity[]>((resolve, reject) => {
						const results: DBChatActivity[] = [];
						const request = conversationIdIndex.openCursor(IDBKeyRange.only(conversationId));

						request.onsuccess = () => {
							const cursor = request.result;
							if (!cursor) {
								resolve(results);
								return;
							}
							const existedActivity = cursor.value as DBChatActivity;
							if (existedActivity.person.id === personId) {
								results.push(existedActivity);
							}
							cursor.continue();
						};

						request.onerror = () => reject(request.error);
					});

					// Delete existing activities to prevent duplicates
					for (const activity of existingActivities) {
						activitiesStore.delete(activity.id);
					}
				}

				for (const activity of oldActivities) {
					activitiesStore.delete(activity.id);
				}

				for (const activity of activities) {
					if (!activity.removedAt) {
						activitiesStore.put(activity);
					} else {
						activitiesStore.delete(activity.id);
					}
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				const conversationIds = uniqueifyData(activities.map(x => x.conversationId)) as string[];

				for (const conversationId of conversationIds) {
					emitDbEvent({ type: "activities-changed", conversationId });
				}
			},
			deleteActivity: async (params: { conversationId: string; personId: string }): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.activities.name, "readwrite");

				const activitiesStore = tx.objectStore(messagingDBConfig.stores.activities.name);

				// Find activities with specific senderId and conversationId
				const conversationIdIndex = activitiesStore.index("conversationId");
				const existingActivities = await new Promise<DBChatActivity[]>((resolve, reject) => {
					const results: DBChatActivity[] = [];
					const request = conversationIdIndex.openCursor(IDBKeyRange.only(params.conversationId));

					request.onsuccess = () => {
						const cursor = request.result;
						if (!cursor) {
							resolve(results);
							return;
						}
						const existedActivity = cursor.value as DBChatActivity;
						if (existedActivity.person.id === params.personId) {
							results.push(existedActivity);
						}
						cursor.continue();
					};

					request.onerror = () => reject(request.error);
				});

				for (const activity of existingActivities) {
					activitiesStore.delete(activity.id);
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "activities-changed", conversationId: params.conversationId });
			},
			deleteActivities: async (ids: string[], conversationId: string): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.activities.name, "readwrite");

				const activitiesStore = tx.objectStore(messagingDBConfig.stores.activities.name);

				for (const id of ids) {
					activitiesStore.delete(id);
				}

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();

				emitDbEvent({ type: "activities-changed", conversationId });
			},
			getConversations: async () => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.conversations.name, "readonly");
				const store = tx.objectStore(messagingDBConfig.stores.conversations.name);
				const conversations = await requestToPromise(store.getAll());
				db.close();
				return (conversations as DBChatModel[]).sort((a, b) => {
					// First, sort by pinned
					if (a.pinned && !b.pinned) return -1;
					if (!a.pinned && b.pinned) return 1;

					// If both have same pinned status, sort by unread
					const isUnreadA =
						a.lastOpenedAt && a?.lastMessage?.sentAt
							? getDateTime(a.lastOpenedAt) < getDateTime(a?.lastMessage?.sentAt)
							: false;
					const isUnreadB =
						b.lastOpenedAt && b?.lastMessage?.sentAt
							? getDateTime(b.lastOpenedAt) < getDateTime(b?.lastMessage?.sentAt)
							: false;

					if (isUnreadA && !isUnreadB) return -1;
					if (!isUnreadA && isUnreadB) return 1;

					// Finally, sort by lastMessage.sentAt in descending order (newest first)
					const sentAtA = a?.lastMessage?.sentAt ?? 0;
					const sentAtB = b?.lastMessage?.sentAt ?? 0;
					return sentAtB - sentAtA;
				});
			},
			getMessagesAroundAnchor: async (
				conversationId: string,
				anchorTimestamp: number | null // in seconds
			): Promise<MessagesWindow> => {
				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.messages.name, "readonly");
				const index = tx
					.objectStore(messagingDBConfig.stores.messages.name)
					.index(messagingDBConfig.stores.messages.sortIndex);

				try {
					const minId = "";
					const maxId = "\uffff";
					if (anchorTimestamp == null) {
						const desc = await collectCursorValues<DBChatMessage>(
							() =>
								index.openCursor(
									IDBKeyRange.bound([conversationId, 0, minId], [conversationId, Number.MAX_SAFE_INTEGER, maxId]),
									"prev"
								),
							messagingConfig.initialWindowSize + 1
						);
						const hasOlder = desc.length > messagingConfig.initialWindowSize;
						const items = desc.slice(0, messagingConfig.initialWindowSize).reverse();
						return {
							items: items.filter(x => !x.removedAt),
							hasOlder,
							hasNewer: false,
							anchor: items.length ? messageSortKey(items[items.length - 1]!) : null
						};
					}

					const olderDesc = await collectCursorValues<DBChatMessage>(
						() =>
							index.openCursor(
								IDBKeyRange.bound([conversationId, 0, minId], [conversationId, anchorTimestamp, maxId]),
								"prev"
							),
						messagingConfig.initialOlderPart + 1
					);
					const newerAsc = await collectCursorValues<DBChatMessage>(
						() =>
							index.openCursor(
								IDBKeyRange.bound(
									[conversationId, anchorTimestamp + 1, minId],
									[conversationId, Number.MAX_SAFE_INTEGER, maxId]
								),
								"next"
							),
						messagingConfig.initialNewerPart + 1
					);

					const hasOlder = olderDesc.length > messagingConfig.initialOlderPart;
					const hasNewer = newerAsc.length > messagingConfig.initialNewerPart;
					const older = olderDesc.slice(0, messagingConfig.initialOlderPart).reverse();
					const newer = newerAsc.slice(0, messagingConfig.initialNewerPart);
					const items = [...older, ...newer];

					return {
						items: items.filter(x => !x.removedAt),
						hasOlder,
						hasNewer,
						anchor: items.find(item => !item.removedAt && item.sentAt >= anchorTimestamp)
							? messageSortKey(items.find(item => item.sentAt >= anchorTimestamp) as DBChatMessage)
							: null
					};
				} finally {
					db.close();
				}
			},
			getMessagesByDirection: async ({
				conversationId,
				visibleMessage,
				limit,
				direction = "next"
			}: {
				conversationId: string;
				visibleMessage: DBChatMessage;
				limit?: number;
				direction?: "prev" | "next";
			}) => {
				const msgLimit =
					limit ?? (direction === "next" ? messagingConfig.pageSizeNewer : messagingConfig.pageSizeOlder);

				const db = await openDb();
				const tx = db.transaction(messagingDBConfig.stores.messages.name, "readonly");
				const index = tx
					.objectStore(messagingDBConfig.stores.messages.name)
					.index(messagingDBConfig.stores.messages.sortIndex);

				try {
					const sortDirKey = messageSortKey(visibleMessage);

					const msgSelection =
						direction === "next"
							? await collectCursorValues<DBChatMessage>(
									() =>
										index.openCursor(
											IDBKeyRange.bound(sortDirKey, [conversationId, Number.MAX_SAFE_INTEGER, "￿"]),
											"next"
										),
									msgLimit + 2
								)
							: await collectCursorValues<DBChatMessage>(
									() =>
										index.openCursor(
											IDBKeyRange.bound(
												[conversationId, 0, ""],
												[
													sortDirKey[0],
													sortDirKey[1],
													sortDirKey[2] < "" ? "" : String.fromCharCode(sortDirKey[2].charCodeAt(0) - 1)
												]
											),
											"prev"
										),
									0
								);

					const filtered = msgSelection.filter(item => {
						if (item.removedAt) return false;

						if (direction === "next") {
							if (item.sentAt !== visibleMessage.sentAt) return item.sentAt > visibleMessage.sentAt;
							return item.id > visibleMessage.id;
						} else {
							if (item.sentAt !== visibleMessage.sentAt) return item.sentAt < visibleMessage.sentAt;
							return item.id < visibleMessage.id;
						}
					});
					const hasOther = filtered.length > msgLimit;
					const items = filtered.slice(0, msgLimit);
					if (direction === "prev") {
						items.reverse();
					}
					return { items, hasOther };
				} finally {
					db.close();
				}
			},
			deleteMessage: async (messageId: string): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(
					[messagingDBConfig.stores.conversations.name, messagingDBConfig.stores.messages.name],
					"readwrite"
				);

				const messagesStore = tx.objectStore(messagingDBConfig.stores.messages.name);

				try {
					const message = (await requestToPromise(messagesStore.get(messageId))) as DBChatMessage | undefined;
					if (message) {
						const updatedMessage = {
							...message,
							body: {
								text: "deleted"
							},
							removedAt: Timestamp.now().toMillis()
						};
						messagesStore.put(updatedMessage);

						await new Promise<void>((resolve, reject) => {
							tx.oncomplete = () => resolve();
							tx.onerror = () => reject(tx.error);
							tx.onabort = () => reject(tx.error);
						});

						// await recomputeDialogMeta(message.conversationId);

						emitDbEvent({
							type: "messages-changed",
							conversation: {
								id: message.conversationId,
								messages: [updatedMessage]
							}
						});
						emitDbEvent({ type: "conversations-changed" });
					}
				} finally {
					db.close();
				}
			},
			countUnreadMessagesByCursor: async (index: IDBIndex, conversationId: string, lastOpenedAt: number | null) => {
				return new Promise<number>((resolve, reject) => {
					let count = 0;
					const lower = [conversationId, (lastOpenedAt ?? 0) + 1, ""];
					const upper = [conversationId, Number.MAX_SAFE_INTEGER, "￿"];
					const request = index.openCursor(IDBKeyRange.bound(lower, upper), "next");

					request.onsuccess = () => {
						const cursor = request.result;
						if (!cursor) {
							resolve(count);
							return;
						}
						const message = cursor.value as DBChatMessage;
						// if (!message.isOutgoing) count += 1;
						if (message.status !== MessageStatuses.WAITING) count += 1;
						cursor.continue();
					};

					request.onerror = () => reject(request.error);
				});
			},
			// recomputeConversationMeta: async (conversationId: string): Promise<void> => {
			// 	const db = await openDb();
			// 	const tx = db.transaction(
			// 		[messagingDBConfig.stores.conversations.name, messagingDBConfig.stores.messages.name],
			// 		"readwrite"
			// 	);
			// 	const conversationsStore = tx.objectStore(messagingDBConfig.stores.conversations.name);
			// 	const messagesIndex = tx
			// 		.objectStore(messagingDBConfig.stores.messages.name)
			// 		.index(messagingDBConfig.stores.messages.sortIndex);
			// 	const conversation = (await requestToPromise(conversationsStore.get(conversationId))) as
			// 		| DBChatModel
			// 		| undefined;
			//
			// 	if (!conversation) {
			// 		db.close();
			// 		return;
			// 	}
			//
			// 	try {
			// 		const latest = await collectCursorValues<DBChatMessage>(
			// 			() =>
			// 				messagesIndex.openCursor(
			// 					IDBKeyRange.bound([conversationId, 0, ""], [conversationId, Number.MAX_SAFE_INTEGER, "￿"]),
			// 					"prev"
			// 				),
			// 			1
			// 		);
			// 		const unreadCount = await methods.countUnreadMessagesByCursor(
			// 			messagesIndex,
			// 			conversationId,
			// 			conversation.lastOpenedAt
			// 		);
			//
			// 		conversationsStore.put({
			// 			...conversation,
			// 			updatedAt: latest[0]?.sentAt ?? conversation.updatedAt,
			// 			lastMessageId: latest[0]?.id ?? null,
			// 			lastMessageText: latest[0]?.body?.text ?? null,
			// 			unreadCount
			// 		});
			//
			// 		await new Promise<void>((resolve, reject) => {
			// 			tx.oncomplete = () => resolve();
			// 			tx.onerror = () => reject(tx.error);
			// 			tx.onabort = () => reject(tx.error);
			// 		});
			// 	} finally {
			// 		db.close();
			// 	}
			// },
			cleanUpDB: async (): Promise<void> => {
				const db = await openDb();
				const tx = db.transaction(
					[
						messagingDBConfig.stores.conversations.name,
						messagingDBConfig.stores.messages.name,
						messagingDBConfig.stores.participants.name,
						messagingDBConfig.stores.drafts.name,
						messagingDBConfig.stores.activities.name
					],
					"readwrite"
				);

				const conversationsStore = tx.objectStore(messagingDBConfig.stores.conversations.name);
				const messagesStore = tx.objectStore(messagingDBConfig.stores.messages.name);
				const participantsStore = tx.objectStore(messagingDBConfig.stores.participants.name);
				const draftsStore = tx.objectStore(messagingDBConfig.stores.drafts.name);
				const activitiesStore = tx.objectStore(messagingDBConfig.stores.activities.name);

				conversationsStore.clear();
				messagesStore.clear();
				participantsStore.clear();
				draftsStore.clear();
				activitiesStore.clear();

				await new Promise<void>((resolve, reject) => {
					tx.oncomplete = () => resolve();
					tx.onerror = () => reject(tx.error);
					tx.onabort = () => reject(tx.error);
				});

				db.close();
			}
		}),
		[convertChatMessageToDBMessage, emitDbEvent, openDb]
	);

	return methods;
};

export default useMessagingDB;
